/**
 * The case's Notice Brief, refetched when a notice key fact moves (its
 * issues, their memos, the strategy) or the agent invalidates `key_facts`.
 */
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";

import { queryClient } from "@/lib/query/queryClient";
import { useRealtimeEvent } from "@/lib/realtime/RealtimeProvider";

import { useOnCaseInvalidate } from "../../caseIde/context/caseInvalidation";
import { KeyFactsAPI } from "../api";
import { NOTICE_KINDS } from "../model";
import type { KeyFact, NoticeBriefPayload } from "../types";

export const noticeBriefQueryKey = (individualId: string) =>
  ["notice-brief", individualId] as const;

export const useNoticeBrief = (
  individualId: string,
  enabled: boolean
): {
  data: NoticeBriefPayload | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
} => {
  const query = useQuery(
    {
      queryKey: noticeBriefQueryKey(individualId),
      queryFn: () => KeyFactsAPI.noticeBrief(individualId),
      enabled: enabled && !!individualId
    },
    queryClient
  );
  const refetch = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: noticeBriefQueryKey(individualId)
    });
  }, [individualId]);

  useRealtimeEvent<{ individual_id: string; fact: KeyFact }>(
    "key_fact.updated",
    (data) => {
      if (
        data?.individual_id === individualId &&
        NOTICE_KINDS.has(data.fact?.kind)
      )
        refetch();
    }
  );
  useOnCaseInvalidate(individualId, ["key_facts"], refetch);

  return {
    data: query.data ?? null,
    loading: query.isLoading,
    error: query.error
      ? (query.error as Error).message || "Couldn't load the notice brief"
      : null,
    refetch
  };
};
